import { useId, type CSSProperties } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { LOGO, MARK } from './logo-data';
import { cn } from './util';

const INK = '#111111';
const YELLOW = '#ffde00';
const RED = '#ff4d2e';

export interface LogoProps {
  /** `full` = burst + 20 + FIGHT! bar; `mark` = burst + 20 (favicon-like, for tight headers). */
  variant?: 'full' | 'mark';
  /** Entrance pop + breathing burst (home page). Static by default (headers). */
  animated?: boolean;
  /** CSS width (number = px). Height follows the artwork's aspect ratio. */
  width?: number | string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

const center: CSSProperties = { transformBox: 'fill-box', transformOrigin: '50% 50%' };

/** 20fight "POW!" logo: a red ink-stroked 20 bursting from a yellow starburst, FIGHT! bar at −6°. */
export function Logo({
  variant = 'full',
  animated = false,
  width = 220,
  className,
  style,
  title = '20fight!',
}: LogoProps) {
  const clipId = useId().replace(/:/g, '');
  const reduce = useReducedMotion();
  const live = animated && !reduce;
  const dim = typeof width === 'number' ? `${width}px` : width;

  if (variant === 'mark') {
    return (
      <svg
        role="img"
        aria-label={title}
        viewBox={MARK.viewBox}
        className={cn('inline-block shrink-0 overflow-visible', className)}
        style={{ width: dim, height: 'auto', ...style }}
      >
        <polygon
          points={MARK.burst}
          fill={INK}
          transform="translate(2.5 2.5)"
          stroke={INK}
          strokeWidth="3.5"
          strokeLinejoin="round"
        />
        <polygon
          points={MARK.burst}
          fill={YELLOW}
          stroke={INK}
          strokeWidth="3.5"
          strokeLinejoin="round"
        />
        <path
          d={MARK.d20}
          fill={RED}
          stroke={INK}
          strokeWidth="5"
          strokeLinejoin="round"
          paintOrder="stroke"
          transform="rotate(-6 32 32)"
        />
      </svg>
    );
  }

  const { bar, cx, cy } = LOGO;
  return (
    <svg
      role="img"
      aria-label={title}
      viewBox={LOGO.viewBox}
      className={cn('inline-block shrink-0 overflow-visible', className)}
      style={{ width: dim, height: 'auto', ...style }}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={LOGO.d20} />
        </clipPath>
      </defs>

      <motion.g
        style={center}
        initial={live ? { scale: 0, rotate: -25 } : false}
        animate={live ? { scale: 1, rotate: 0 } : undefined}
        transition={live ? { type: 'spring', stiffness: 260, damping: 14 } : undefined}
      >
        <motion.g
          style={center}
          animate={live ? { scale: [1, 1.04, 1], rotate: [0, 1.2, 0] } : undefined}
          transition={
            live ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.9 } : undefined
          }
        >
          <polygon
            points={LOGO.outer}
            fill={INK}
            stroke={INK}
            strokeWidth="8"
            strokeLinejoin="round"
            transform="translate(9 9)"
          />
          <polygon
            points={LOGO.outer}
            fill={YELLOW}
            stroke={INK}
            strokeWidth="8"
            strokeLinejoin="round"
          />
          <polygon points={LOGO.inner} fill="#fff6b0" />
        </motion.g>
      </motion.g>

      <g transform={`rotate(-5 ${cx} ${cy - 8})`}>
        <motion.g
          style={center}
          initial={live ? { scale: 0.2, y: 40, opacity: 0 } : false}
          animate={live ? { scale: 1, y: 0, opacity: 1 } : undefined}
          transition={
            live ? { type: 'spring', stiffness: 420, damping: 13, delay: 0.18 } : undefined
          }
        >
          <path
            d={LOGO.d20}
            fill={INK}
            stroke={INK}
            strokeWidth="14"
            strokeLinejoin="round"
            transform="translate(8 8)"
          />
          <path
            d={LOGO.d20}
            fill={RED}
            stroke={INK}
            strokeWidth="14"
            strokeLinejoin="round"
            paintOrder="stroke"
          />
          <g clipPath={`url(#${clipId})`}>
            <path d={LOGO.d20} fill="#ffb4a3" />
            <path d={LOGO.d20} fill={RED} transform="translate(4 5)" />
          </g>
        </motion.g>
      </g>

      <g transform={`rotate(-6 ${cx} ${bar.y + bar.h / 2})`}>
        <motion.g
          initial={live ? { x: -420, rotate: -14 } : false}
          animate={live ? { x: 0, rotate: 0 } : undefined}
          transition={
            live ? { type: 'spring', stiffness: 520, damping: 22, delay: 0.42 } : undefined
          }
          style={center}
        >
          <rect x={bar.x + 7} y={bar.y + 7} width={bar.w} height={bar.h} fill={INK} opacity="0.3" />
          <rect
            x={bar.x}
            y={bar.y}
            width={bar.w}
            height={bar.h}
            fill={INK}
            stroke={INK}
            strokeWidth="4"
          />
          <rect
            x={bar.x + 6}
            y={bar.y + 6}
            width={bar.w - 12}
            height={bar.h - 12}
            fill="none"
            stroke={YELLOW}
            strokeWidth="2.5"
          />
          <path d={LOGO.dF} fill="#ffffff" />
        </motion.g>
      </g>
    </svg>
  );
}
